/**
 * src/context/RoleVersionContext.tsx
 *
 * Shared version counter for the authenticated Customer role (AC-005,
 * WP-ATTICDEV-032-DRIVER-004).
 *
 * Problem: multiple independent useCustomerProfile() instances (BottomTabBar,
 * TopNav, ProfileScreen) each hold isolated profile state. When DevRoleSwitcher
 * writes a new role to HarperDB, only its local displayedRole updates -- other
 * instances remain stale, so the nav tabs do not change without a page reload.
 *
 * Solution: a single source of truth for "a role change just happened". Any
 * component that calls useCustomerProfile() subscribes to roleVersion. When
 * DevRoleSwitcher commits a successful role write, it calls bumpRoleVersion().
 * useCustomerProfile() includes roleVersion in its fetchProfile deps: when the
 * version increments, fetchProfile gets a new reference, and the
 * useEffect([fetchProfile]) fires immediately, re-fetching every subscribed
 * instance in the same React paint cycle.
 *
 * Result: TopNav, BottomTabBar, and ProfileScreen all receive the new role
 * from HarperDB within one round trip -- no page reload needed.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useState,
} from "react";

interface RoleVersionContextValue {
  /** Increments each time a role change is committed to HarperDB. */
  roleVersion: number;
  /** Call after a successful role update to trigger refetches in all consumers. */
  bumpRoleVersion: () => void;
}

const RoleVersionContext = createContext<RoleVersionContextValue | null>(null);

interface RoleVersionProviderProps {
  children: React.ReactNode;
}

export function RoleVersionProvider({ children }: RoleVersionProviderProps) {
  const [roleVersion, setRoleVersion] = useState(0);
  const bumpRoleVersion = useCallback(() => setRoleVersion((v) => v + 1), []);

  return (
    <RoleVersionContext.Provider value={{ roleVersion, bumpRoleVersion }}>
      {children}
    </RoleVersionContext.Provider>
  );
}

/**
 * useRoleVersion -- consume the shared role-change counter.
 *
 * Must be used within a <RoleVersionProvider>. Called by:
 *   - useCustomerProfile: subscribes to roleVersion to trigger re-fetches
 *   - DevRoleSwitcher: publishes role changes via bumpRoleVersion()
 */
export function useRoleVersion(): RoleVersionContextValue {
  const ctx = useContext(RoleVersionContext);
  if (!ctx) {
    throw new Error("useRoleVersion must be used within RoleVersionProvider");
  }
  return ctx;
}
